import { Link } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import Seo from "@/components/feature/Seo";
import CharacterCard from "@/components/feature/CharacterCard";
import FaqAccordion from "@/components/feature/FaqAccordion";
import { characters, relationships, faqs } from "@/mocks/characters";

const jsonLd = [
  {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "继母与继姐·人物图鉴",
    description:
      "继母与继姐漫画人物关系全梳理，主角介绍、继母角色、继姐角色设定详解。",
    url: "/",
    inLanguage: "zh-CN",
  },
  {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: "继母与继姐漫画角色图鉴 - 首页",
    url: "/",
    inLanguage: "zh-CN",
  },
];

export default function Home() {
  return (
    <Layout>
      <Seo
        title="继母与继姐漫画角色图鉴 - 人物关系·主角介绍·继母继姐设定详解"
        description="继母与继姐漫画人物关系全梳理：从主角介绍到继母角色、继姐角色设定一次看全。理清人物关系网与角色背景，点开即读继母与继姐漫画角色设定详解。"
        keywords="继母与继姐漫画,继母与继姐漫画角色,继母与继姐漫画人物关系,继母与继姐漫画主角介绍,继母与继姐漫画继母角色,继母与继姐漫画继姐角色"
        jsonLd={jsonLd}
      />
      {/* ===== 首屏 ===== */}
      <section className="relative flex min-h-[86vh] items-center justify-center overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Warm%20editorial%20illustration%20of%20a%20modern%20reorganized%20family%20gathering%20around%20a%20wooden%20dining%20table%20in%20soft%20morning%20light%2C%20three%20female%20figures%20sharing%20a%20quiet%20warm%20moment%2C%20manhwa%20style%20soft%20lineart%2C%20cream%20and%20terra%20cotta%20palette%2C%20abstract%20stylized%20composition%2C%20cozy%20cinematic%20atmosphere%2C%20high%20detail%2C%20no%20text&width=1600&height=900&seq=home-hero&orientation=landscape"
          alt="继母与继姐漫画重组家庭温暖场景插画"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-primary-950/70 via-primary-900/55 to-primary-950/80"></div>

        <div className="relative z-10 mx-auto w-full max-w-4xl px-4 py-24 text-center md:px-6">
          <span className="inline-flex items-center gap-2 rounded-full border border-background-50/30 bg-background-50/10 px-4 py-1.5 text-xs tracking-widest text-background-50 backdrop-blur">
            <i className="ri-book-2-line"></i> 继母与继姐漫画 · 角色与关系图鉴
          </span>
          <h1 className="mt-6 font-heading text-3xl font-black leading-snug text-background-50 md:text-5xl md:leading-tight">
            理清「继母与继姐」<br className="md:hidden" />人物关系与角色设定
          </h1>
          <p className="mx-auto mt-5 max-w-2xl text-base leading-8 text-background-100/90 md:text-lg">
            从主角介绍，到继母角色、继姐角色的人物设定，一次看全。拆解三个人物如何从试探、对峙，
            走到彼此理解，共同拼出完整的关系网。
          </p>
          <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Link
              to="/characters"
              className="whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-400"
            >
              查看角色详解
            </Link>
            <Link
              to="/relationships"
              className="whitespace-nowrap rounded-md border border-background-50/40 bg-background-50/10 px-6 py-3 text-sm font-medium text-background-50 backdrop-blur transition-colors hover:bg-background-50/20"
            >
              查看人物关系
            </Link>
          </div>

          <div className="mx-auto mt-12 grid max-w-2xl grid-cols-3 gap-3">
            {[
              { n: "3", t: "核心人物" },
              { n: "3", t: "关系主线" },
              { n: "N", t: "成长节点" },
            ].map((s) => (
              <div
                key={s.t}
                className="rounded-lg border border-background-50/20 bg-background-50/10 px-3 py-4 backdrop-blur"
              >
                <p className="font-heading text-2xl font-bold text-accent-300 md:text-3xl">{s.n}</p>
                <p className="mt-1 text-xs text-background-100/80">{s.t}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== 作品简介 ===== */}
      <section className="mx-auto max-w-6xl px-4 py-14 md:px-6 md:py-20">
        <div className="grid items-center gap-8 lg:grid-cols-2 lg:gap-14">
          <div>
            <p className="text-xs font-semibold tracking-[0.3em] text-primary-600">ABOUT THE STORY</p>
            <h2 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              一个关于「重新成为家人」的故事
            </h2>
            <p className="mt-5 text-sm leading-7 text-foreground-700 md:text-base md:leading-8">
              「继母与继姐」是一部聚焦重组家庭情感的作品。它没有刻意放大冲突，而是把镜头对准日常里的
              试探、退让、误解与和解，让读者在平凡相处中读懂人与人之间缓慢而珍贵的靠近。
            </p>
            <p className="mt-4 text-sm leading-7 text-foreground-700 md:text-base md:leading-8">
              看懂这部作品，关键在于理清三位核心人物的关系网：主角如何在陌生新家里寻找归属，
              继母如何用克制的方式守护这段关系，继姐又如何在倔强与柔软之间成长。本图鉴正是为此而生。
            </p>
            <div className="mt-6 flex flex-wrap gap-2">
              {["重组家庭", "人物关系", "成长治愈", "角色设定"].map((t) => (
                <span
                  key={t}
                  className="rounded-md bg-secondary-100 px-3 py-1.5 text-xs font-medium text-secondary-800"
                >
                  # {t}
                </span>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 md:gap-4">
            <div className="overflow-hidden rounded-lg border border-background-200">
              <img
                src="https://readdy.ai/api/search-image?query=Warm%20manhwa%20illustration%20of%20a%20young%20woman%20looking%20out%20a%20sunlit%20window%2C%20reflective%20and%20quiet%20mood%2C%20cream%20and%20terra%20cotta%20tones%2C%20soft%20lineart%2C%20editorial%20style%2C%20cozy%20domestic%20atmosphere%2C%20high%20detail%2C%20no%20text&width=520&height=520&seq=home-story-1&orientation=squarish"
                alt="主角在重组家庭中安静沉思的场景"
                className="h-full w-full object-cover"
              />
            </div>
            <div className="overflow-hidden rounded-lg border border-background-200">
              <img
                src="https://readdy.ai/api/search-image?query=Warm%20manhwa%20illustration%20of%20two%20women%20sharing%20tea%20at%20a%20kitchen%20counter%2C%20gentle%20tentative%20bonding%20moment%2C%20cream%20beige%20and%20warm%20gold%20palette%2C%20soft%20lineart%2C%20editorial%20cozy%20domestic%20scene%2C%20high%20detail%2C%20no%20text&width=520&height=520&seq=home-story-2&orientation=squarish"
                alt="继母与主角在厨房喝茶交流的温暖场景"
                className="h-full w-full object-cover"
              />
            </div>
            <div className="col-span-2 overflow-hidden rounded-lg border border-background-200">
              <img
                src="https://readdy.ai/api/search-image?query=Warm%20manhwa%20illustration%20of%20a%20young%20woman%20and%20a%20cooler%20young%20woman%20walking%20side%20by%20side%20on%20an%20autumn%20street%2C%20awkward%20yet%20softening%20bond%2C%20warm%20earth%20tones%2C%20soft%20lineart%2C%20editorial%20storybook%20feel%2C%20high%20detail%2C%20no%20text&width=1080&height=520&seq=home-story-3&orientation=landscape"
                alt="主角与继姐并肩走在秋日街道上逐渐熟悉"
                className="h-full w-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ===== 核心角色 ===== */}
      <section className="bg-background-100 py-14 md:py-20">
        <div className="mx-auto max-w-6xl px-4 md:px-6">
          <div className="flex flex-col items-start justify-between gap-4 md:flex-row md:items-end">
            <div>
              <p className="text-xs font-semibold tracking-[0.3em] text-primary-600">MAIN CHARACTERS</p>
              <h2 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                核心角色 · 人物设定一览
              </h2>
              <p className="mt-3 max-w-xl text-sm leading-7 text-foreground-600">
                主角、继母角色、继姐角色，三位人物构成了完整的人物关系网。点开即可了解各自的背景、性格与成长。
              </p>
            </div>
            <Link
              to="/characters"
              className="inline-flex items-center gap-1 whitespace-nowrap text-sm font-medium text-primary-700 transition-colors hover:text-primary-600"
            >
              查看全部角色 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>

          <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {characters.map((c) => (
              <CharacterCard key={c.id} character={c} />
            ))}
          </div>
        </div>
      </section>

      {/* ===== 人物关系预览 ===== */}
      <section className="mx-auto max-w-6xl px-4 py-14 md:px-6 md:py-20">
        <div className="flex flex-col items-start justify-between gap-4 md:flex-row md:items-end">
          <div>
            <p className="text-xs font-semibold tracking-[0.3em] text-primary-600">RELATIONSHIP WEB</p>
            <h2 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              人物关系 · 关系网速览
            </h2>
            <p className="mt-3 max-w-xl text-sm leading-7 text-foreground-600">
              主角、继母、继姐三人彼此牵动，三段关系各有各的温度与节奏。
            </p>
          </div>
          <Link
            to="/relationships"
            className="inline-flex items-center gap-1 whitespace-nowrap text-sm font-medium text-primary-700 transition-colors hover:text-primary-600"
          >
            查看关系详解 <i className="ri-arrow-right-line"></i>
          </Link>
        </div>

        <div className="mt-8 grid gap-4 md:grid-cols-3">
          {relationships.map((r) => (
            <Link
              key={r.pair}
              to="/relationships"
              className="group rounded-lg border border-background-200 bg-background-50 p-5 transition-all duration-300 hover:-translate-y-1 hover:border-primary-300"
            >
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-primary-600">{r.tone}</span>
                <span className="grid h-8 w-8 items-center justify-center rounded-full bg-secondary-100 text-sm text-secondary-700">
                  <i className="ri-link"></i>
                </span>
              </div>
              <h3 className="mt-3 font-heading text-lg font-bold text-foreground-950">{r.pair}</h3>
              <p className="mt-2 text-sm leading-6 text-foreground-700">{r.description}</p>
              <p className="mt-4 inline-flex items-center gap-1 text-xs font-medium text-primary-700">
                {r.status} <i className="ri-arrow-right-line group-hover:translate-x-0.5 transition-transform"></i>
              </p>
            </Link>
          ))}
        </div>
      </section>

      {/* ===== 常见问题 ===== */}
      <section className="bg-background-100 py-14 md:py-20">
        <div className="mx-auto max-w-3xl px-4 md:px-6">
          <div className="text-center">
            <p className="text-xs font-semibold tracking-[0.3em] text-primary-600">FAQ</p>
            <h2 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              读者常见问题
            </h2>
            <p className="mx-auto mt-3 max-w-xl text-sm leading-7 text-foreground-600">
              关于人物关系与角色设定的高频疑问，这里一次讲清楚。
            </p>
          </div>
          <div className="mt-8">
            <FaqAccordion items={faqs} />
          </div>
          <div className="mt-6 text-center">
            <Link
              to="/faq"
              className="inline-flex items-center gap-1 whitespace-nowrap text-sm font-medium text-primary-700 transition-colors hover:text-primary-600"
            >
              查看更多常见问题 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* ===== 口碑 / 召唤行动 ===== */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary-700 to-primary-900"></div>
        <div className="relative z-10 mx-auto max-w-4xl px-4 py-16 text-center md:px-6 md:py-20">
          <span className="mx-auto grid h-12 w-12 items-center justify-center rounded-full bg-background-50/10 text-2xl text-accent-300">
            <i className="ri-double-quotes-l"></i>
          </span>
          <blockquote className="mx-auto mt-6 max-w-2xl font-heading text-xl font-medium leading-9 text-background-50 md:text-2xl md:leading-10">
            「原本人物关系总理不清，看了图鉴里主角、继母与继姐的设定梳理，
            终于读懂了这段关系网的每一处伏笔。」
          </blockquote>
          <p className="mt-4 text-sm text-background-100/80">—— 读者阅读笔记</p>
          <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Link
              to="/relationships"
              className="whitespace-nowrap rounded-md bg-accent-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-accent-400"
            >
              理清关系网
            </Link>
            <Link
              to="/characters"
              className="whitespace-nowrap rounded-md border border-background-50/40 bg-background-50/10 px-6 py-3 text-sm font-medium text-background-50 backdrop-blur transition-colors hover:bg-background-50/20"
            >
              认识每一位角色
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
}